import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, Grid, Paper, Typography,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import type { Incident } from '../../services/incidentsApi';

export interface IncidentSuppressDialogProps {
  open: boolean;
  onClose: () => void;
  selectedIncident: Incident | null;
  drawerBusy: string | null;
  canManage: boolean;
  incidentTypeLabel: (type: string | undefined) => string;
  onSuppress: (params: { durationSec: number; reason: string }) => Promise<void>;
}

const IncidentSuppressDialog: React.FC<IncidentSuppressDialogProps> = ({
  open, onClose, selectedIncident, drawerBusy, canManage, incidentTypeLabel, onSuppress,
}) => {
  const [reason, setReason] = useState('');
  const [durationSec, setDurationSec] = useState(3600);

  useEffect(() => {
    if (open) {
      setReason('');
      setDurationSec(3600);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Snooze zone / similar incidents</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This creates a time-boxed snooze in cloud, records it in audit, and best-effort applies it to the edge.
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Reason"
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select
                label="Duration"
                value={String(durationSec)}
                onChange={(e) => setDurationSec(Number(e.target.value))}
              >
                <MenuItem value={900}>15 minutes</MenuItem>
                <MenuItem value={3600}>1 hour</MenuItem>
                <MenuItem value={14400}>4 hours</MenuItem>
                <MenuItem value={86400}>1 day</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="textSecondary">Scope (auto)</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {incidentTypeLabel(selectedIncident?.incident_type)}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                Zone: {selectedIncident?.zone_id || '—'}
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                Camera: {selectedIncident?.camera_id || '—'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedIncident?.incident_id || !!drawerBusy || !canManage}
          onClick={() => onSuppress({ durationSec, reason })}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IncidentSuppressDialog;
